import React, { Component } from "react";
import isEqual from "lodash/isEqual";

import Divider from "@material-ui/core/Divider";
import Paper from "@material-ui/core/Paper";
import { withStyles } from "@material-ui/styles";

import ActionSave from "@material-ui/icons/Save";
import ActionBack from "@material-ui/icons/ArrowBack";

import {
  PageHeader,
  PageFooter,
  TextInput,
  NumberInput,
  ReferenceInput,
  AutoCompleteInput,
  SelectInput,
  Button,
  WithReduxForm,
  FormDataConsumer,
  showNotification
} from "jazasoft";
import { SimpleForm } from "jazasoft/lib/mui/form/SimpleForm";

import CuChecklistItemController from "./CuChecklistItemController";

import Table, { TextInput as MyTextInput, Toolbar } from "../../../components/Table";

import { ChecklistType } from "../../../utils/types";

const format = (record, cuChecklist) => {
  if (!record) return {};
  if (!record.estimation) return record;
  const part =
    record.estimation.parts.find(p => {
      let itemColor = record.styleColor;
      if (!itemColor) {
        const itemWithColor = cuChecklist.checkListItems.find(e => e.styleColor && e.itemNo === record.itemNo);
        itemColor = itemWithColor && itemWithColor.styleColor;
      }
      return p.styleColor === itemColor && p.placement === record.fabricPlacement;
    }) || {};
  return {
    ...record,
    extra: part.extra,
    wastage: part.wastage,
    tentativeExtra: part.tentativeExtra,
    tentativeWastage: part.tentativeWastage
  };
};

const inputOptions = {
  xs: 12,
  sm: 3,
  fullWidth: true,
  options: { fullWidth: true }
};

const styles = theme => ({
  root: {
    margin: theme.spacing(3),
    padding: theme.spacing(3)
  }
});

/**
 *  SizeBreakup:
 *      If State = New, then Estimation has not been created
 *          Provide Table to edit SizeBreakup of CuChecklistItem
 *      Else If Estimation is created, state will not be new and estimationId will be assigned
 *          Provide Table to edit Actual SizeBreakup of Estimation,
 *          if there are two flows and tentative estimation is completed (i.e tentativeFabricRequired > 0) and automated marker has not been generated yet
 *  Extra and Wastage:
 *      If State = "ConsumptionAvailable" || "ReportAvailable", make it editable
 *      Once  both values are updated for each part of the parent estimation, change state to "ReportAvailable"
 *      If any value is updated blank, reverse back to consumption available
 */
class ChecklistItemEditView extends Component {
  state = {
    columns: [],
    rows: [],
    sizeCount: 5,
    title: ""
  };

  componentDidMount() {
    this.init();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!isEqual(this.props.record, nextProps.record)) {
      this.init(nextProps);
    }
  }

  init = (props = this.props) => {
    const { record, cuChecklist } = props;
    if (!record || !record.customer || !record.season) return; //Not Shell part
    let columns = [],
      rows = [],
      sizeCount = 5,
      title = "";
    if (record.state === "New") {
      let sizeBreakup = JSON.parse(record.sizeBreakup);

      sizeCount = Object.keys(sizeBreakup).length;
      sizeCount = sizeCount % 5 === 0 ? (sizeCount / 5 + 1) * 5 : 5 * Math.ceil(sizeCount / 5);
      columns = Array(sizeCount)
        .fill(0)
        .map((e, idx) => ({ title: `Size${idx + 1}`, dataKey: `size${idx + 1}` }));

      const headerRow = Object.keys(sizeBreakup)
        .sort((key1, key2) => sizeBreakup[key1].serial - sizeBreakup[key2].serial)
        .reduce((acc, size, idx) => ({ ...acc, [`size${idx + 1}`]: size }), {});
      const qtyRow = Object.keys(sizeBreakup)
        .sort((key1, key2) => sizeBreakup[key1].serial - sizeBreakup[key2].serial)
        .reduce((acc, size, idx) => ({ ...acc, [`size${idx + 1}`]: sizeBreakup[size].qty }), {});
      const total = record.total || Object.keys(sizeBreakup).reduce((acc, size) => acc + sizeBreakup[size].qty, 0);

      if (cuChecklist.checklistType === ChecklistType.ESTIMATION && total < 15) {
        columns.push({ title: "Total", dataKey: "total" });
      }

      rows = [headerRow, { ...qtyRow, total }];
      title = "Size Breakup";
    } else if (record.estimation) {
      let locked =
        !record.tentativeFabricRequired ||
        record.estimation.markers.filter(m => m.markerType === "Automated").length > 0;
      if (!locked && record.estimation.tentativeSizeBreakup) {
        if (record.estimation.sizeBreakup) {
          const breakupPerColor = JSON.parse(record.estimation.sizeBreakup);
          const colors = Object.keys(breakupPerColor);
          let anySizeBreakup;
          colors.forEach(color => {
            anySizeBreakup = anySizeBreakup || breakupPerColor[color];
          });

          const headerRow = anySizeBreakup
            ? Object.keys(anySizeBreakup)
                .sort((key1, key2) => anySizeBreakup[key1].serial - anySizeBreakup[key2].serial)
                .reduce((acc, size, idx) => ({ ...acc, [`size${idx + 1}`]: size }), {})
            : [];

          const sizeBreakup = breakupPerColor[record.styleColor];
          if (sizeBreakup) {
            sizeCount = Object.keys(sizeBreakup).length;
            sizeCount = sizeCount % 5 === 0 ? (sizeCount / 5 + 1) * 5 : 5 * Math.ceil(sizeCount / 5);
            columns = Array(sizeCount)
              .fill(0)
              .map((e, idx) => ({ title: `Size${idx + 1}`, dataKey: `size${idx + 1}` }));
            const qtyRow = Object.keys(sizeBreakup)
              .sort((key1, key2) => sizeBreakup[key1].serial - sizeBreakup[key2].serial)
              .reduce((acc, size, idx) => ({ ...acc, [`size${idx + 1}`]: sizeBreakup[size].qty }), {});
            rows = [headerRow, qtyRow];
          } else {
            sizeCount = 10;
            columns = Array(sizeCount)
              .fill(0)
              .map((e, idx) => ({ title: `Size${idx + 1}`, dataKey: `size${idx + 1}` }));
            rows = [headerRow, {}];
          }
        } else {
          sizeCount = 10;
          columns = Array(sizeCount)
            .fill(0)
            .map((e, idx) => ({ title: `Size${idx + 1}`, dataKey: `size${idx + 1}` }));
          rows = [{}, {}];
        }
        title = "Actual Size Breakup";
      }
    }

    columns = columns.map(c => ({ ...c, element: <MyTextInput width={75} onChange={this.onChange} /> }));
    this.setState({ columns, rows, sizeCount, title });
  };

  onColumnAdd = () => {
    let sizeCount = this.state.sizeCount;
    sizeCount += 5;
    let columns = Array(sizeCount)
      .fill(0)
      .map((e, idx) => ({ title: `Size${idx + 1}`, dataKey: `size${idx + 1}` }));
    if (this.props.record && this.props.record.state === "New") {
      columns.push({ title: "Total", dataKey: "total" });
    }
    columns = columns.map(c => ({ ...c, element: <MyTextInput width={75} onChange={this.onChange} /> }));
    this.setState({ columns, sizeCount });
  };

  onSubmit = handleSubmit => () => {
    handleSubmit(values => {
      let redirect = false;

      let estimation = values.estimation;
      if (this.state.rows.length > 0) {
        let { tentativeOrderQty, sizeBreakup, error } = this.processSizeBreakup();
        if (error) {
          this.props.dispatch(showNotification(error, { type: "danger" }));
          return;
        }

        if (values.state === "New") {
          values = { ...values, sizeBreakup: JSON.stringify(sizeBreakup), total: tentativeOrderQty };
        } else if (estimation) {
          if (values.tentativeFabricRequired > 0) {
            let breakupPerColor = JSON.parse(estimation.sizeBreakup || "{}");
            breakupPerColor = { ...breakupPerColor, [values.styleColor]: sizeBreakup };
            estimation = { ...estimation, sizeBreakup: JSON.stringify(breakupPerColor) };
          } else {
            let breakupPerColor = JSON.parse(estimation.tentativeSizeBreakup || "{}");
            breakupPerColor = { ...breakupPerColor, [values.styleColor]: sizeBreakup };
            let orderQtyPerColor = JSON.parse(estimation.tentativeOrderQty || "{}");
            orderQtyPerColor = { ...orderQtyPerColor, [values.styleColor]: tentativeOrderQty };
            estimation = {
              ...estimation,
              tentativeSizeBreakup: JSON.stringify(breakupPerColor),
              tentativeOrderQty: JSON.stringify(orderQtyPerColor)
            };
          }
        }
      }

      if (estimation) {
        // previous part
        let part = this.props.record.estimation.parts.find(
          p => (!values.styleColor || p.styleColor === values.styleColor) && p.placement === values.fabricPlacement
        );
        // updated part
        let estPart = estimation.parts.find(
          p => (!values.styleColor || p.styleColor === values.styleColor) && p.placement === values.fabricPlacement
        );
        if (
          this.props.record.estimation.costingId !== values.costingId ||
          (estPart &&
            part &&
            (part.tentativeExtra !== values.tentativeExtra ||
              part.tentativeWastage !== values.tentativeWastage ||
              part.extra !== values.extra ||
              part.wastage !== values.wastage))
        ) {
          estimation.costingId = values.constingId;

          estPart.tentativeExtra =
            part.tentativeExtra !== values.tentativeExtra ? values.tentativeExtra : estPart.tentativeExtra;
          estPart.tentativeWastage =
            part.tentativeWastage !== values.tentativeWastage ? values.tentativeWastage : estPart.tentativeWastage;
          estPart.extra = part.extra !== values.extra ? values.extra : estPart.extra;
          estPart.wastage = part.wastage !== values.wastage ? values.wastage : estPart.wastage;
        }
      }
      if (estimation) {
        this.props.updateEstimation(estimation, () => {
          if (redirect) {
            this.onBack();
          } else {
            redirect = true;
          }
        });
      } else {
        redirect = true;
      }
      this.props.updateCuChecklistItem(values, () => {
        if (this.state.rows.length === 0 || redirect) {
          this.onBack();
        } else {
          redirect = true;
        }
      });
    })();
  };

  processSizeBreakup = () => {
    let sizeBreakup = {},
      tentativeOrderQty = 0;
    const { rows } = this.state;
    if (rows.length === 0) return {};
    const { total: t, ...headerRow } = rows[0];
    const sizeKeys = Object.keys(headerRow).filter(key => headerRow[key] && headerRow[key].trim() !== "");

    const { total, ...row } = rows[1];

    sizeBreakup = sizeKeys.reduce(
      (acc, key, idx) => ({ ...acc, [headerRow[key]]: { serial: idx + 1, qty: row[key] } }),
      {}
    );
    tentativeOrderQty = typeof total === "string" && total.trim() !== "" ? parseInt(total.trim()) : total;

    let error = false;
    if (
      Object.keys(sizeBreakup).filter(
        size =>
          !sizeBreakup[size].qty || (typeof sizeBreakup[size].qty === "string" && sizeBreakup[size].qty.trim() === "")
      ).length > 0
    ) {
      error = "Fill order qty for all sizes.";
    }

    if (t) {
      if (!tentativeOrderQty || (typeof tentativeOrderQty === "string" && tentativeOrderQty.trim() === "")) {
        error = "Enter Total order qty.";
      }
    }
    if (sizeKeys.length === 0) {
      error = "Size entry required.";
    }
    sizeBreakup = Object.keys(sizeBreakup).reduce(
      (acc, size) => ({
        ...acc,
        [size]: { ...sizeBreakup[size], qty: parseInt(sizeBreakup[size].qty) }
      }),
      {}
    );
    return { sizeBreakup, tentativeOrderQty, error };
  };

  onChange =
    ({ rowIdx, column }) =>
    e => {
      const value = e.target.value;
      this.setState(({ rows }) => {
        let newRows = [...rows];
        const row = { ...newRows[rowIdx], [column.dataKey]: value };
        newRows.splice(rowIdx, 1, row);
        return { rows: newRows };
      });
    };

  onBack = () => {
    this.props.history.push(`/estimation/cuChecklists/${this.props.id}/view`);
  };

  render() {
    const { classes, cuChecklist = {}, products, fabricPatterns, fabricCategories, saving } = this.props;
    const { columns, rows, title } = this.state;

    const record = format(this.props.record, cuChecklist);
    const disabled = record.state !== "New";
    const disabledTrim = record.customer === null || record.season === null;
    const disabledTentative =
      record.state === "New" ||
      record.state === "In Progress" ||
      (record.tentativeFabricRequired > 0 && record.estimation && record.estimation.sizeBreakup !== null) ||
      (record.estimation && record.estimation.markers.filter(m => m.markerType === "Manual").length === 0);

    const disabledActual =
      !record.estimation ||
      record.state === "New" ||
      record.state === "In Progress" ||
      (record.estimation.tentativeSizeBreakup && !record.tentativeFabricRequired) ||
      record.estimation.markers.filter(m => m.markerType === "Automated").length === 0;
    // console.log({ record, disabledActual, disabled, products, fabricPatterns, fabricCategories });
    // console.log({rows, columns});
    return (
      <div>
        <PageHeader title={`Checklist Item #${record.id}`} />

        <Paper className={classes.root} square>
          <WithReduxForm initialValues={record}>
            {({ handleSubmit, ...rest }) => (
              <div>
                <SimpleForm component="div" footer={false}>
                  <TextInput label="Customer" source="customer.name" disabled={true} {...inputOptions} />
                  <ReferenceInput
                    source="seasonId"
                    reference="seasons"
                    disabled={disabled || disabledTrim}
                    {...inputOptions}>
                    <AutoCompleteInput disabled={disabled || disabledTrim} />
                  </ReferenceInput>
                  <SelectInput
                    source="product"
                    choices={products.map(e => ({ id: e.name, name: e.name }))}
                    disabled={disabled || disabledTrim}
                    {...inputOptions}
                  />
                  <TextInput source="customerPoRef" disabled={disabled || disabledTrim} {...inputOptions} />
                  <TextInput source="styleName" disabled={disabled || disabledTrim} {...inputOptions} />
                  <TextInput source="styleColor" disabled={disabled || disabledTrim} {...inputOptions} />
                  <TextInput source="fit" disabled={disabled || disabledTrim} {...inputOptions} />
                  <TextInput source="gradedSpecSheet" disabled={disabled || disabledTrim} {...inputOptions} />
                  <TextInput source="matchingParameter" disabled={disabled || disabledTrim} {...inputOptions} />
                  <TextInput source="sampleProvided" disabled={disabled || disabledTrim} {...inputOptions} />
                  <ReferenceInput source="millId" reference="mills" disabled={disabled} {...inputOptions}>
                    <AutoCompleteInput />
                  </ReferenceInput>
                  <TextInput source="fabricArticle" disabled={disabled} {...inputOptions} />
                  <TextInput source="fabricItemCode" disabled={disabled} {...inputOptions} />
                  <TextInput source="fabricPlacement" disabled={disabled} {...inputOptions} />
                  <SelectInput
                    source="fabricType"
                    choices={fabricPatterns.map(e => ({ id: e.name, name: e.name }))}
                    disabled={disabled}
                    {...inputOptions}
                  />

                  <SelectInput
                    source="fabricCategory"
                    choices={fabricCategories.map(e => ({ id: e.name, name: e.name }))}
                    disabled={disabled}
                    {...inputOptions}
                  />
                  <TextInput source="fabricComposition" disabled={disabled} {...inputOptions} />
                  <TextInput source="csv" disabled={disabled} {...inputOptions} />
                  <NumberInput source="width" disabled={disabled} {...inputOptions} />
                  <NumberInput source="repeatLength" disabled={disabled} {...inputOptions} />
                  <NumberInput source="repeatWidth" disabled={disabled} {...inputOptions} />
                  <NumberInput source="warpShrinkage" disabled={disabled} {...inputOptions} />
                  <NumberInput source="weftShrinkage" disabled={disabled} {...inputOptions} />
                  <TextInput label="Fabric Direction" source="way" disabled={disabled} {...inputOptions} />
                  <TextInput source="fabricDescription" disabled={disabled} {...inputOptions} />
                  <TextInput source="styleDesc" disabled={disabled} {...inputOptions} />
                  <TextInput source="remarks" disabled={disabled} {...inputOptions} />
                  {cuChecklist.checklistType === ChecklistType.ESTIMATION && (
                    <ReferenceInput label="Costing Id" source="costingId" reference="costings" {...inputOptions}>
                      <AutoCompleteInput optionText="cstId" />
                    </ReferenceInput>
                  )}
                  {cuChecklist.checklistType === ChecklistType.ESTIMATION && (
                    <FormDataConsumer xs={12} sm={3}>
                      {({ formData }) =>
                        !formData.costingId ? <NumberInput source="costingCu" options={{ fullWidth: true }} /> : null
                      }
                    </FormDataConsumer>
                  )}

                  {cuChecklist.checklistType === ChecklistType.ESTIMATION && (
                    <NumberInput source="tentativeExtra" disabled={disabledTentative} {...inputOptions} />
                  )}
                  {cuChecklist.checklistType === ChecklistType.ESTIMATION && (
                    <NumberInput source="tentativeWastage" disabled={disabledTentative} {...inputOptions} />
                  )}

                  {cuChecklist.checklistType === ChecklistType.ESTIMATION && (
                    <NumberInput source="extra" disabled={disabledActual} {...inputOptions} />
                  )}
                  {cuChecklist.checklistType === ChecklistType.ESTIMATION && (
                    <NumberInput source="wastage" disabled={disabledActual} {...inputOptions} />
                  )}
                </SimpleForm>

                {rows && columns && rows.length > 0 && (
                  <Paper style={{ margin: "1em 0" }} square>
                    <Toolbar title={title} actions={[{ name: "add", onClick: this.onColumnAdd }]} />
                    <Divider />
                    <div style={{ overflowX: "auto" }}>
                      <Table columns={columns} rows={rows} fixedHeader={false} />
                    </div>
                  </Paper>
                )}

                <PageFooter>
                  <Button
                    label="Save"
                    variant="contained"
                    color="primary"
                    disabled={saving}
                    onClick={this.onSubmit(handleSubmit)}>
                    <ActionSave />
                  </Button>
                  <Button label="Back" variant="contained" onClick={this.onBack} style={{ marginLeft: 16 }}>
                    <ActionBack />
                  </Button>
                </PageFooter>
              </div>
            )}
          </WithReduxForm>
        </Paper>
      </div>
    );
  }
}

const StyledChecklistItemEdit = withStyles(styles)(ChecklistItemEditView);

const ChecklistItemEdit = props => (
  <CuChecklistItemController {...props}>
    {controllerProps => <StyledChecklistItemEdit {...props} {...controllerProps} />}
  </CuChecklistItemController>
);

export default ChecklistItemEdit;
